<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">{{ pageTitle }}</h1>
      <button class="btn btn-secondary" @click="handleBack">
        <i class="ri-arrow-left-line"></i>
        返回
      </button>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="handleBack">返回</button>
    </div>

    <div v-else class="form-container">
      <form @submit.prevent="handleSubmit">
        <!-- 會員選擇 -->
        <div class="form-section">
          <h2 class="section-title">選擇會員</h2>
          
          <div class="form-group">
            <label class="form-label">搜尋會員 <span class="required">*</span></label>
            <div class="member-search">
              <input
                v-model="memberSearchQuery"
                type="text"
                class="form-control"
                placeholder="輸入會員名稱、電話或 Email 搜尋..."
                @input="handleMemberSearch"
              />
              <i class="ri-search-line"></i>
            </div>
            <small class="form-text">輸入會員名稱、電話或 Email 進行搜尋</small>
          </div>

          <!-- 會員搜尋結果 -->
          <div v-if="memberSearchQuery && memberSearchResults.length > 0" class="member-results">
            <div class="results-header">
              <span>找到 {{ memberSearchResults.length }} 位會員</span>
            </div>
            <div class="member-list">
              <div
                v-for="member in memberSearchResults"
                :key="member.id"
                class="member-item"
                :class="{ selected: selectedMember?.id === member.id }"
                @click="selectMember(member)"
              >
                <div class="member-info">
                  <div class="member-name">{{ member.name }}</div>
                  <div class="member-details">
                    <span v-if="member.phone">{{ member.phone }}</span>
                    <span v-if="member.email">{{ member.email }}</span>
                  </div>
                </div>
                <div v-if="selectedMember?.id === member.id" class="check-icon">
                  <i class="ri-check-line"></i>
                </div>
              </div>
            </div>
          </div>

          <div v-if="memberSearchQuery && !memberSearching && memberSearchResults.length === 0" class="no-results">
            <i class="ri-user-search-line"></i>
            <p>找不到符合的會員</p>
          </div>

          <!-- 已選擇的會員 -->
          <div v-if="selectedMember" class="selected-member-card">
            <div class="card-header">
              <h3>已選擇的會員</h3>
              <button type="button" class="btn btn-sm btn-link" @click="clearMember">
                <i class="ri-close-line"></i>
                清除
              </button>
            </div>
            <div class="member-details">
              <div class="detail-item">
                <span class="label">姓名：</span>
                <span class="value">{{ selectedMember.name }}</span>
              </div>
              <div v-if="selectedMember.phone" class="detail-item">
                <span class="label">電話：</span>
                <span class="value">{{ selectedMember.phone }}</span>
              </div>
              <div v-if="selectedMember.email" class="detail-item">
                <span class="label">Email：</span>
                <span class="value">{{ selectedMember.email }}</span>
              </div>
            </div>
          </div>

          <div v-if="errors.memberId" class="error-text">{{ errors.memberId }}</div>
        </div>

        <!-- 優惠券模板選擇 -->
        <div class="form-section">
          <h2 class="section-title">選擇優惠券模板</h2>
          
          <div class="form-group">
            <label class="form-label">優惠券模板 <span class="required">*</span></label>
            <select
              v-model="form.templateId"
              class="form-control"
              :class="{ 'is-invalid': errors.templateId }"
              required
              :disabled="templatesLoading"
              @change="handleTemplateChange"
            >
              <option value="">請選擇優惠券模板</option>
              <option v-for="template in templates" :key="template.id" :value="template.id">
                {{ template.templateName }} - {{ formatCouponTypeText(template.couponType) }} {{ formatDiscountValue(template) }}
              </option>
            </select>
            <div v-if="templatesLoading" class="form-text">載入中...</div>
            <div v-if="errors.templateId" class="invalid-feedback">{{ errors.templateId }}</div>
          </div>

          <!-- 模板資訊 -->
          <div v-if="selectedTemplate" class="template-info-card">
            <div class="card-header">
              <h3>模板資訊</h3>
            </div>
            <div class="template-details">
              <div class="detail-row">
                <span class="label">模板名稱：</span>
                <span class="value">{{ selectedTemplate.templateName }}</span>
              </div>
              <div class="detail-row">
                <span class="label">優惠券類型：</span>
                <span :class="getCouponTypeBadgeClass(selectedTemplate.couponType)">
                  {{ formatCouponTypeText(selectedTemplate.couponType) }}
                </span>
              </div>
              <div class="detail-row">
                <span class="label">折扣值：</span>
                <span class="value">{{ formatDiscountValue(selectedTemplate) }}</span>
              </div>
              <div v-if="selectedTemplate.minPurchaseAmount > 0" class="detail-row">
                <span class="label">最低消費：</span>
                <span class="value">${{ formatNumber(selectedTemplate.minPurchaseAmount) }}</span>
              </div>
              <div class="detail-row">
                <span class="label">有效期：</span>
                <span class="value">{{ formatValidityPeriod(selectedTemplate) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 發放設定 -->
        <div class="form-section">
          <h2 class="section-title">發放設定</h2>
          
          <div class="form-group">
            <label class="form-label">發放數量 <span class="required">*</span></label>
            <input
              v-model.number="form.quantity"
              type="number"
              class="form-control"
              :class="{ 'is-invalid': errors.quantity }"
              placeholder="1"
              min="1"
              max="10"
              required
            />
            <small class="form-text">可發放 1-10 張優惠券</small>
            <div v-if="errors.quantity" class="invalid-feedback">{{ errors.quantity }}</div>
          </div>

          <div class="form-group">
            <label class="form-label">發放原因（選填）</label>
            <textarea
              v-model="form.reason"
              class="form-control"
              rows="3"
              placeholder="請輸入發放原因，例如：補發優惠券、會員補償等"
              maxlength="200"
            ></textarea>
            <small class="form-text">最多 200 字</small>
          </div>
        </div>

        <!-- 錯誤訊息 -->
        <div v-if="submitError" class="alert alert-danger">
          {{ submitError }}
        </div>

        <!-- 操作按鈕 -->
        <div class="form-actions">
          <button
            type="button"
            class="btn btn-secondary"
            @click="handleBack"
            :disabled="submitting"
          >
            取消
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="submitting || !selectedMember || !form.templateId"
          >
            <span v-if="submitting">
              <i class="ri-loader-4-line spin"></i>
              發放中...
            </span>
            <span v-else>
              <i class="ri-gift-line"></i>
              確認發放
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { couponService } from '@/api/modules/coupons.js';
import { memberService } from '@/api/modules/members.js';
import { showSuccess, showError } from '@/utils/swal.js';

export default {
  name: 'ManualIssueCoupon',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('手動發放優惠券');
    
    const loading = ref(false);
    const error = ref(null);
    const submitting = ref(false);
    const submitError = ref('');
    const templates = ref([]);
    const templatesLoading = ref(false);
    const memberSearchQuery = ref('');
    const memberSearchResults = ref([]);
    const memberSearching = ref(false);
    const selectedMember = ref(null);
    const searchTimeout = ref(null);

    const form = reactive({
      templateId: '',
      quantity: 1,
      reason: ''
    });

    const errors = reactive({});

    const selectedTemplate = computed(() => {
      return templates.value.find(t => t.id === form.templateId);
    });

    const fetchTemplates = async () => {
      templatesLoading.value = true;
      try {
        const response = await couponService.getTemplates({ pageSize: 100, isActive: true });
        if (response && response.success && response.data) {
          templates.value = response.data.items || [];
        }
      } catch (err) {
        console.error('獲取優惠券模板失敗:', err);
        showError('獲取優惠券模板失敗');
      } finally {
        templatesLoading.value = false;
      }
    };

    const handleMemberSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }

      if (!memberSearchQuery.value || memberSearchQuery.value.length < 2) {
        memberSearchResults.value = [];
        return;
      }

      searchTimeout.value = setTimeout(async () => {
        memberSearching.value = true;
        try {
          const response = await memberService.getMembers({
            search: memberSearchQuery.value,
            pageSize: 20
          });
          
          if (response && response.success && response.data) {
            memberSearchResults.value = response.data.items || [];
          }
        } catch (err) {
          console.error('搜尋會員失敗:', err);
          memberSearchResults.value = [];
        } finally {
          memberSearching.value = false;
        }
      }, 500);
    };

    const selectMember = (member) => {
      selectedMember.value = member;
      memberSearchQuery.value = member.name;
      memberSearchResults.value = [];
    };

    const clearMember = () => {
      selectedMember.value = null;
      memberSearchQuery.value = '';
      memberSearchResults.value = [];
    };

    const handleTemplateChange = () => {
      errors.templateId = '';
    };

    const validateForm = () => {
      errors.memberId = '';
      errors.templateId = '';
      errors.quantity = '';

      if (!selectedMember.value) {
        errors.memberId = '請選擇會員';
        return false;
      }

      if (!form.templateId) {
        errors.templateId = '請選擇優惠券模板';
        return false;
      }

      if (!form.quantity || form.quantity < 1 || form.quantity > 10) {
        errors.quantity = '發放數量必須在 1-10 之間';
        return false;
      }

      return true;
    };

    const handleSubmit = async () => {
      if (!validateForm()) {
        return;
      }

      submitting.value = true;
      submitError.value = '';

      try {
        const response = await couponService.issueCoupon({
          memberId: selectedMember.value.id,
          templateId: form.templateId,
          quantity: form.quantity,
          reason: form.reason || undefined
        });

        if (response && response.success) {
          await showSuccess(`成功發放 ${form.quantity} 張優惠券給 ${selectedMember.value.name}`);
          router.push('/client/coupons/issues');
        } else {
          submitError.value = response?.message || '發放失敗';
        }
      } catch (err) {
        console.error('發放優惠券失敗:', err);
        submitError.value = err.message || '發放失敗';
        showError(submitError.value);
      } finally {
        submitting.value = false;
      }
    };

    const handleBack = () => {
      router.back();
    };

    const formatCouponTypeText = (type) => {
      const typeMap = {
        discount: '折扣券',
        fixed_amount: '折價券',
        free_shipping: '運費券',
        gift: '贈品券'
      };
      return typeMap[type] || type;
    };

    const formatDiscountValue = (template) => {
      if (template.couponType === 'discount') {
        return `${template.discountValue}%`;
      } else if (template.couponType === 'fixed_amount') {
        return `$${Number(template.discountValue).toLocaleString('zh-TW')}`;
      } else if (template.couponType === 'free_shipping') {
        return '免運費';
      } else if (template.couponType === 'gift') {
        return '贈品';
      }
      return '-';
    };

    const formatValidityPeriod = (template) => {
      if (template.validityDays) {
        return `發放後 ${template.validityDays} 天有效`;
      } else if (template.fixedStartDate && template.fixedEndDate) {
        return `${template.fixedStartDate} ~ ${template.fixedEndDate}`;
      }
      return '-';
    };

    const formatNumber = (num) => {
      if (num === null || num === undefined) return '0';
      return Number(num).toLocaleString('zh-TW');
    };

    const getCouponTypeBadgeClass = (type) => {
      const classMap = {
        discount: 'badge badge-primary',
        fixed_amount: 'badge badge-success',
        free_shipping: 'badge badge-info',
        gift: 'badge badge-warning'
      };
      return classMap[type] || 'badge badge-secondary';
    };

    onMounted(() => {
      fetchTemplates();
    });

    return {
      pageTitle,
      loading,
      error,
      submitting,
      submitError,
      templates,
      templatesLoading,
      memberSearchQuery,
      memberSearchResults,
      memberSearching,
      selectedMember,
      form,
      errors,
      selectedTemplate,
      handleMemberSearch,
      selectMember,
      clearMember,
      handleTemplateChange,
      handleSubmit,
      handleBack,
      formatCouponTypeText,
      formatDiscountValue,
      formatValidityPeriod,
      formatNumber,
      getCouponTypeBadgeClass
    };
  }
};
</script>

<style scoped>
.form-container {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.form-section {
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e9ecef;
}

.form-section:last-child {
  border-bottom: none;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}

.required {
  color: #dc3545;
}

.member-search {
  position: relative;
}

.member-search i {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  pointer-events: none;
}

.member-results {
  margin-top: 16px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
}

.results-header {
  padding: 12px 16px;
  background: #f8f9fa;
  font-size: 14px;
  font-weight: 500;
  color: #666;
  border-bottom: 1px solid #e9ecef;
}

.member-list {
  max-height: 300px;
  overflow-y: auto;
}

.member-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f0f0f0;
}

.member-item:hover {
  background: #f8f9fa;
}

.member-item.selected {
  background: #e7f3ff;
  border-color: #b3d9ff;
}

.member-item:last-child {
  border-bottom: none;
}

.member-info {
  flex: 1;
}

.member-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.member-details {
  font-size: 13px;
  color: #666;
  display: flex;
  gap: 12px;
}

.check-icon {
  color: #28a745;
  font-size: 20px;
}

.no-results {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.no-results i {
  font-size: 48px;
  margin-bottom: 12px;
  display: block;
}

.selected-member-card,
.template-info-card {
  margin-top: 16px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.card-header h3 {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.member-details,
.template-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-item,
.detail-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.detail-item .label,
.detail-row .label {
  color: #666;
  font-weight: 500;
  min-width: 100px;
}

.detail-item .value,
.detail-row .value {
  color: #333;
}

.template-info-card .detail-row {
  flex-wrap: wrap;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e9ecef;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.error-text {
  color: #dc3545;
  font-size: 14px;
  margin-top: 4px;
}

.alert {
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.alert-danger {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}
</style>
